import { differenceInHours, format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useMemo } from "react";
import { strings } from "@/I18n";
import { formatDuration } from "@/utils/dateUtils";
import { formatStatusText } from "@/utils/statusColors";
import { useSavedCityFullInfo } from "@/v2/stores";
import { formatCurrency as formatCurrencyUtil } from "@/v2/ui/primitives/currency";
import { parseBackendTimestamp } from "@/v2/utils/backend-dates";

interface UseFormattersReturn {
  formatDate: (date?: string | null, format?: string) => string;
  formatDateWithTimezone: (date?: string | null, timezone?: string) => string;
  formatRelativeDate: (date?: string | null) => string;
  formatCurrency: (value: number, currency: string, divide?: boolean) => string;
  formatCurrencyWithSign: (
    value: number,
    currency: string,
    divide?: boolean
  ) => string;
  formatVehicleType: (type?: string | null) => string | null;
  formatBrand: (brand?: string | null) => string | null;
  formatStatus: (status?: string | null) => string;
  formatLockStatus: (isOpen?: boolean | null) => string;
  formatDuration: (hours: number) => string;
  formatPhoneNumber: (phone?: string | null) => string | null;
}

/**
 * Converts Moment.js format tokens to date-fns format tokens.
 * Common patterns used in the codebase.
 */
function convertMomentFormatToDateFns(momentFormat: string): string {
  return momentFormat
    .replace(/YYYY/g, "yyyy")
    .replace(/YY/g, "yy")
    .replace(/DD/g, "dd")
    .replace(/D/g, "d")
    .replace(/HH/g, "HH")
    .replace(/hh/g, "hh")
    .replace(/mm/g, "mm")
    .replace(/ss/g, "ss")
    .replace(/A/g, "a")
    .replace(/a/g, "aaa");
}

/**
 * Backend (bikesharing-journey) emits naive UTC timestamps with no offset.
 * Append `Z` when missing so date-fns parses them as UTC instead of browser-local.
 * Idempotent for `Z` and explicit `+HH:MM` offsets so a future backend
 * contract fix (OffsetDateTime) doesn't break this code.
 */
export function normalizeDate(date: string): Date {
  return parseBackendTimestamp(date);
}

export function useFormatters(): UseFormattersReturn {
  const savedCityFullInfo = useSavedCityFullInfo();

  // Get timezone from city or use default (UTC)
  // Note: savedCityFullInfo may have timezone in the full API response even though the base type doesn't include it
  const timezone = useMemo(() => {
    const cityData = savedCityFullInfo as { timezone?: string } | null;
    return cityData?.timezone ?? "UTC";
  }, [savedCityFullInfo]);

  const formatDate = useCallback(
    (date?: string | null, dateFormat?: string): string => {
      if (!date) return strings("common.notApplicable");

      // Default format matching original Moment.js 'MMM D, YYYY HH:mm'
      const defaultFormat = "MMM d, yyyy HH:mm";
      const formatPattern = dateFormat
        ? convertMomentFormatToDateFns(dateFormat)
        : defaultFormat;

      try {
        const parsedDate = normalizeDate(date);
        return format(parsedDate, formatPattern);
      } catch {
        return strings("common.invalidDate");
      }
    },
    []
  );

  const formatDateWithTimezone = useCallback(
    (date?: string | null, timezoneParam?: string): string => {
      if (!date) return strings("common.notApplicable");

      const tz = timezoneParam || timezone;

      try {
        const parsedDate = normalizeDate(date);
        // Format: 'MMM d, yyyy HH:mm' matches original Moment.js 'MMM D, YYYY HH:mm'
        return formatInTimeZone(parsedDate, tz, "MMM d, yyyy HH:mm");
      } catch {
        return formatDate(date);
      }
    },
    [timezone, formatDate]
  );

  const formatRelativeDate = useCallback((date?: string | null): string => {
    if (!date) return strings("common.never");

    try {
      const parsedDate = normalizeDate(date);
      const hoursSince = differenceInHours(new Date(), parsedDate);
      return formatDuration(hoursSince);
    } catch {
      return strings("common.invalidDate");
    }
  }, []);

  const formatCurrency = useCallback(
    (value: number, currency: string, divide = false): string => {
      try {
        const result = formatCurrencyUtil(value, currency, divide);
        return `${result.sign}${result.value} ${result.currency}`;
      } catch {
        return `${value} ${currency}`;
      }
    },
    []
  );

  const formatCurrencyWithSign = useCallback(
    (value: number, currency: string, divide = false): string => {
      try {
        const result = formatCurrencyUtil(value, currency, divide);
        let sign = "";
        if (value > 0) {
          sign = "+";
        } else if (value < 0) {
          sign = "-";
        }
        return `${sign}${result.value} ${result.currency}`;
      } catch {
        let sign = "";
        if (value > 0) {
          sign = "+";
        } else if (value < 0) {
          sign = "-";
        }
        return `${sign}${value} ${currency}`;
      }
    },
    []
  );

  const formatVehicleType = useCallback(
    (type?: string | null): string | null => {
      if (!type) return null;

      const typeMap: Record<string, string> = {
        E_BIKE: "E-Bike",
        E_SCOOTER: "E-Scooter",
        SCOOTER: "Scooter",
        CARGO: "Cargo",
        BIKE: "Bike",
        E_BIKE_50: "E-Bike 50",
        E_BIKE_25: "E-Bike 25",
        E_BIKE_15: "E-Bike 15",
      };

      return (
        typeMap[type] ||
        type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, " ")
      );
    },
    []
  );

  const formatBrand = useCallback((brand?: string | null): string | null => {
    if (!brand) return null;

    return brand
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }, []);

  const formatStatus = useCallback((status?: string | null): string => {
    return formatStatusText(status);
  }, []);

  const formatLockStatus = useCallback((isOpen?: boolean | null): string => {
    if (isOpen == null) return strings("common.unknown");
    return isOpen ? strings("common.unlocked") : strings("common.locked");
  }, []);

  const formatPhoneNumber = useCallback(
    (phone?: string | null): string | null => {
      if (!phone) return null;

      // Remove all non-digit characters
      const cleaned = phone.replace(/\D/g, "");

      // Format based on length
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      } else if (cleaned.length === 11) {
        return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      }

      // Return original if doesn't match expected patterns
      return phone;
    },
    []
  );

  return {
    formatDate,
    formatDateWithTimezone,
    formatRelativeDate,
    formatCurrency,
    formatCurrencyWithSign,
    formatVehicleType,
    formatBrand,
    formatStatus,
    formatLockStatus,
    formatDuration,
    formatPhoneNumber,
  };
}
